import { useState, useEffect } from 'react';
import { preferencesApi } from '../lib/api';
import { encryptData, decryptData, hashData, getSecretKey } from '../lib/encryption';

interface PreferencesData {
  // Job search criteria
  titles: string[];
  industries: string[];
  locations: string[];
  remotePreference: 'remote' | 'hybrid' | 'onsite' | 'any';
  salaryMin?: number;
  experienceLevel: 'entry' | 'mid' | 'senior' | 'lead' | 'any';

  // Additional preferences
  companySize: string[];
  excludeCompanies: string[];
  mustHaveKeywords: string[];
  niceToHaveKeywords: string[];
  dealBreakers: string[];

  // Free-form notes
  additionalNotes: string;
}

const defaultPreferences: PreferencesData = {
  titles: [],
  industries: [],
  locations: [],
  remotePreference: 'any',
  experienceLevel: 'any',
  companySize: [],
  excludeCompanies: [],
  mustHaveKeywords: [],
  niceToHaveKeywords: [],
  dealBreakers: [],
  additionalNotes: '',
};

// Convert structured preferences to human-readable text for AI summarization
function preferencesToText(prefs: PreferencesData): string {
  const parts: string[] = [];

  if (prefs.titles.length > 0) {
    parts.push(`Target job titles: ${prefs.titles.join(', ')}`);
  }

  if (prefs.industries.length > 0) {
    parts.push(`Preferred industries: ${prefs.industries.join(', ')}`);
  }

  if (prefs.locations.length > 0) {
    parts.push(`Preferred locations: ${prefs.locations.join(', ')}`);
  }

  if (prefs.remotePreference !== 'any') {
    const remoteLabels = {
      remote: 'Remote only',
      hybrid: 'Hybrid work',
      onsite: 'On-site only',
    };
    parts.push(`Work arrangement: ${remoteLabels[prefs.remotePreference]}`);
  }

  if (prefs.experienceLevel !== 'any') {
    const levelLabels = {
      entry: 'Entry level (0-2 years)',
      mid: 'Mid level (2-5 years)',
      senior: 'Senior (5-10 years)',
      lead: 'Lead/Principal (10+ years)',
    };
    parts.push(`Experience level: ${levelLabels[prefs.experienceLevel]}`);
  }

  if (prefs.salaryMin) {
    parts.push(`Minimum salary: $${prefs.salaryMin.toLocaleString()}`);
  }

  if (prefs.mustHaveKeywords.length > 0) {
    parts.push(`Must-have skills/keywords: ${prefs.mustHaveKeywords.join(', ')}`);
  }

  if (prefs.niceToHaveKeywords.length > 0) {
    parts.push(`Nice-to-have skills: ${prefs.niceToHaveKeywords.join(', ')}`);
  }

  if (prefs.dealBreakers.length > 0) {
    parts.push(`Deal breakers: ${prefs.dealBreakers.join(', ')}`);
  }

  if (prefs.excludeCompanies.length > 0) {
    parts.push(`Companies to exclude: ${prefs.excludeCompanies.join(', ')}`);
  }

  if (prefs.additionalNotes) {
    parts.push(`Additional notes: ${prefs.additionalNotes}`);
  }

  return parts.join('\n');
}

export default function PreferencesEditor() {
  const [preferences, setPreferences] = useState<PreferencesData>(defaultPreferences);
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Input states for array fields
  const [titleInput, setTitleInput] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [excludeInput, setExcludeInput] = useState('');
  const [mustHaveInput, setMustHaveInput] = useState('');
  const [dealBreakerInput, setDealBreakerInput] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  async function loadPreferences() {
    try {
      const secretKey = getSecretKey();
      if (!secretKey) {
        throw new Error('Encryption key not found. Please log in again.');
      }

      const data = await preferencesApi.get();

      // Decrypt preferences
      const decrypted = decryptData(data.encryptedPreferences, secretKey);
      if (decrypted) {
        setPreferences(JSON.parse(decrypted));
      }

      setSummary(data.preferencesSummary);
    } catch {
      // No preferences yet, use defaults
      setPreferences(defaultPreferences);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const secretKey = getSecretKey();
      if (!secretKey) {
        throw new Error('Encryption key not found. Please log in again.');
      }

      // Serialize and hash
      const prefsJson = JSON.stringify(preferences);
      const prefsHash = await hashData(prefsJson);

      // Encrypt
      const encrypted = encryptData(prefsJson, secretKey);

      // Convert to human-readable text for AI summarization
      const prefsText = preferencesToText(preferences);

      // Save with plaintext for summarization
      const result = await preferencesApi.update(encrypted, prefsHash, prefsText);

      setSuccess(
        result.message || 'Preferences saved successfully!'
      );

      // Clear summary if hash changed (will be regenerated)
      if (result.hashChanged) {
        setSummary(null);
        // Reload after delay to get new summary
        setTimeout(async () => {
          try {
            const data = await preferencesApi.get();
            setSummary(data.preferencesSummary);
          } catch {
            // Ignore
          }
        }, 5000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  }

  function addToArray(field: keyof PreferencesData, value: string) {
    if (!value.trim()) return;
    const current = preferences[field] as string[];
    if (!current.includes(value.trim())) {
      setPreferences({
        ...preferences,
        [field]: [...current, value.trim()],
      });
    }
  }

  function removeFromArray(field: keyof PreferencesData, value: string) {
    const current = preferences[field] as string[];
    setPreferences({
      ...preferences,
      [field]: current.filter((v) => v !== value),
    });
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner border-primary-600 border-t-transparent h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Job Preferences</h1>
        <p className="mt-1 text-gray-500">
          Tell us what kind of jobs you're looking for. This helps our AI find the best matches.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {/* AI Summary */}
      {summary && (
        <div className="card bg-primary-50 border-primary-200">
          <h2 className="text-sm font-medium text-primary-800">AI Understanding</h2>
          <p className="mt-2 text-sm text-primary-700">{summary}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Job Titles */}
        <div className="card">
          <label className="label">Target Job Titles</label>
          <p className="text-sm text-gray-500 mb-2">What positions are you looking for?</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addToArray('titles', titleInput);
                  setTitleInput('');
                }
              }}
              placeholder="e.g., Software Engineer, Data Scientist"
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => {
                addToArray('titles', titleInput);
                setTitleInput('');
              }}
              className="btn btn-secondary"
            >
              Add
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {preferences.titles.map((title) => (
              <span key={title} className="badge badge-info flex items-center gap-1">
                {title}
                <button onClick={() => removeFromArray('titles', title)} className="ml-1 hover:text-red-600">
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Industries */}
        <div className="card">
          <label className="label">Industries</label>
          <p className="text-sm text-gray-500 mb-2">Which industries interest you?</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={industryInput}
              onChange={(e) => setIndustryInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addToArray('industries', industryInput);
                  setIndustryInput('');
                }
              }}
              placeholder="e.g., FinTech, Healthcare, AI/ML"
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => {
                addToArray('industries', industryInput);
                setIndustryInput('');
              }}
              className="btn btn-secondary"
            >
              Add
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {preferences.industries.map((industry) => (
              <span key={industry} className="badge badge-info flex items-center gap-1">
                {industry}
                <button onClick={() => removeFromArray('industries', industry)} className="ml-1 hover:text-red-600">
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Locations */}
        <div className="card">
          <label className="label">Preferred Locations</label>
          <p className="text-sm text-gray-500 mb-2">Where would you like to work?</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addToArray('locations', locationInput);
                  setLocationInput('');
                }
              }}
              placeholder="e.g., San Francisco, New York, Remote"
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => {
                addToArray('locations', locationInput);
                setLocationInput('');
              }}
              className="btn btn-secondary"
            >
              Add
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {preferences.locations.map((location) => (
              <span key={location} className="badge badge-info flex items-center gap-1">
                {location}
                <button onClick={() => removeFromArray('locations', location)} className="ml-1 hover:text-red-600">
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Remote Preference */}
        <div className="card">
          <label className="label">Work Arrangement</label>
          <select
            value={preferences.remotePreference}
            onChange={(e) => setPreferences({ ...preferences, remotePreference: e.target.value as PreferencesData['remotePreference'] })}
            className="input"
          >
            <option value="any">No preference</option>
            <option value="remote">Remote only</option>
            <option value="hybrid">Hybrid</option>
            <option value="onsite">On-site only</option>
          </select>
        </div>

        {/* Experience Level */}
        <div className="card">
          <label className="label">Experience Level</label>
          <select
            value={preferences.experienceLevel}
            onChange={(e) => setPreferences({ ...preferences, experienceLevel: e.target.value as PreferencesData['experienceLevel'] })}
            className="input"
          >
            <option value="any">Any level</option>
            <option value="entry">Entry level (0-2 years)</option>
            <option value="mid">Mid level (2-5 years)</option>
            <option value="senior">Senior (5-10 years)</option>
            <option value="lead">Lead/Principal (10+ years)</option>
          </select>
        </div>

        {/* Minimum Salary */}
        <div className="card">
          <label className="label">Minimum Salary (optional)</label>
          <input
            type="number"
            value={preferences.salaryMin || ''}
            onChange={(e) => setPreferences({ ...preferences, salaryMin: e.target.value ? parseInt(e.target.value) : undefined })}
            placeholder="e.g., 100000"
            className="input"
          />
        </div>

        {/* Must Have Keywords */}
        <div className="card">
          <label className="label">Must-Have Skills/Keywords</label>
          <p className="text-sm text-gray-500 mb-2">Skills or technologies you want in job descriptions</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={mustHaveInput}
              onChange={(e) => setMustHaveInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addToArray('mustHaveKeywords', mustHaveInput);
                  setMustHaveInput('');
                }
              }}
              placeholder="e.g., Python, Machine Learning"
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => {
                addToArray('mustHaveKeywords', mustHaveInput);
                setMustHaveInput('');
              }}
              className="btn btn-secondary"
            >
              Add
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {preferences.mustHaveKeywords.map((kw) => (
              <span key={kw} className="badge badge-success flex items-center gap-1">
                {kw}
                <button onClick={() => removeFromArray('mustHaveKeywords', kw)} className="ml-1 hover:text-red-600">
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Deal Breakers */}
        <div className="card">
          <label className="label">Deal Breakers</label>
          <p className="text-sm text-gray-500 mb-2">Keywords or requirements that disqualify a job</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={dealBreakerInput}
              onChange={(e) => setDealBreakerInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addToArray('dealBreakers', dealBreakerInput);
                  setDealBreakerInput('');
                }
              }}
              placeholder="e.g., security clearance required, PHP"
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => {
                addToArray('dealBreakers', dealBreakerInput);
                setDealBreakerInput('');
              }}
              className="btn btn-secondary"
            >
              Add
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {preferences.dealBreakers.map((db) => (
              <span key={db} className="badge badge-error flex items-center gap-1">
                {db}
                <button onClick={() => removeFromArray('dealBreakers', db)} className="ml-1 hover:text-white">
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Companies to Exclude */}
        <div className="card">
          <label className="label">Companies to Exclude</label>
          <p className="text-sm text-gray-500 mb-2">Companies you don't want to see jobs from</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={excludeInput}
              onChange={(e) => setExcludeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addToArray('excludeCompanies', excludeInput);
                  setExcludeInput('');
                }
              }}
              placeholder="e.g., Company Name"
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => {
                addToArray('excludeCompanies', excludeInput);
                setExcludeInput('');
              }}
              className="btn btn-secondary"
            >
              Add
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {preferences.excludeCompanies.map((company) => (
              <span key={company} className="badge bg-gray-100 text-gray-800 flex items-center gap-1">
                {company}
                <button onClick={() => removeFromArray('excludeCompanies', company)} className="ml-1 hover:text-red-600">
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Additional Notes */}
        <div className="card">
          <label className="label">Additional Notes</label>
          <p className="text-sm text-gray-500 mb-2">Any other preferences or context for the AI</p>
          <textarea
            value={preferences.additionalNotes}
            onChange={(e) => setPreferences({ ...preferences, additionalNotes: e.target.value })}
            rows={4}
            placeholder="e.g., I prefer startups over large corporations. Looking for roles with mentorship opportunities..."
            className="input"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving} className="btn btn-primary">
            {saving ? (
              <>
                <div className="spinner mr-2" />
                Saving...
              </>
            ) : (
              'Save Preferences'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
